import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, ExternalLink, Eye, Users as UsersIcon, QrCode, Download, Link as LinkIcon, Save, X as XIcon, AtSign, Phone, Facebook, MessageSquare, RefreshCw, Share2, Package } from "lucide-react";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import ImagePicker from "../components/ImagePicker";

export default function PropertyBoost() {
  const [boosts, setBoosts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [qrFor, setQrFor] = useState(null);
  const [smsFor, setSmsFor] = useState(null);
  const [storyFor, setStoryFor] = useState(null);
  // Per-boost manual cache-buster. Bumped by the "refresh preview" icon on
  // each card so agents can force-reload the OG raster after re-editing the
  // source photo in Photoshop / Lightroom without having to change any
  // preview-critical field on the boost itself.
  const [ogBust, setOgBust] = useState({});

  const load = () => api.get("/boosts").then(r => setBoosts(r.data || []));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/boosts", { ...form, price: parseFloat(form.price) || 0, beds: parseInt(form.beds) || 0, baths: parseFloat(form.baths) || 0, sqft: parseInt(form.sqft) || 0 });
      toast.success("Boost created");
      setForm(emptyForm());
      setCreating(false);
      load();
    } catch { toast.error("Create failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this boost?")) return;
    await api.delete(`/boosts/${id}`);
    load();
  };

  const publicUrl = (slug) => `${window.location.origin}/l/${slug}`;
  const shareUrl = (slug) => `${window.location.origin}/api/share/boost/${slug}`;
  const copyLink = (slug) => { navigator.clipboard.writeText(publicUrl(slug)); toast.success("Link copied"); };

  // What FB / LinkedIn will actually render as the share card. Always routed
  // through `/api/og/boost/{slug}` so the browser sees the exact same
  // resized (≤ 5 MB / 1200×630) JPEG the crawlers do — with graceful raster
  // fallback baked into the endpoint. Cache-buster changes only when a
  // preview-critical field or the hero URL changes.
  const ogPreviewUrl = (b) => {
    const bust = encodeURIComponent(`${b.images?.[0] || ""}|${b.title || ""}|${b.price || 0}|${b.beds || 0}|${b.baths || 0}|${b.sqft || 0}|${ogBust[b.id] || ""}`);
    return `${window.location.origin}/api/og/boost/${b.slug}?v=${bust}`;
  };

  const refreshOgPreview = async (b) => {
    // 1. Bump the client cache-buster so the <img> refetches immediately.
    // 2. Ping the same URL with `cache: "reload"` so the browser + edge CDN
    //    proxy actually re-hit our backend (which re-downloads the hero and
    //    re-optimizes on every request — no server-side cache to bust).
    const ts = Date.now();
    setOgBust((prev) => ({ ...prev, [b.id]: ts }));
    try {
      const bust = encodeURIComponent(`${b.images?.[0] || ""}|${b.title || ""}|${b.price || 0}|${b.beds || 0}|${b.baths || 0}|${b.sqft || 0}|${ts}`);
      await fetch(`${window.location.origin}/api/og/boost/${b.slug}?v=${bust}`, { cache: "reload" });
      toast.success("Preview refreshed — click Preview on FB to also clear Facebook's cache.");
    } catch {
      toast.error("Refresh failed");
    }
  };

  // Default pre-branded SMS caption. Agents can edit before Messages opens
  // via the SMS Caption Editor modal (see `SmsCaptionModal`).
  const defaultSmsCaption = (b) => {
    const parts = [
      b.title ? `New listing: ${b.title}` : "New listing",
      b.address ? b.address : "",
      b.price ? `Priced at ${fmtMoney(b.price)}.` : "",
      "Take a look:",
      publicUrl(b.slug),
      "— CORE Realty and Associates",
    ].filter(Boolean);
    return parts.join(" \n");
  };

  return (
    <div className="space-y-7" data-testid="boost-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Lead Generation</div>
          <h1 className="font-heading text-5xl mt-2">Property Boost</h1>
          <p className="text-sm text-[#050303]/70 mt-2">Create shareable listing pages. Every visitor form becomes a lead + a contact.</p>
        </div>
        <button onClick={() => setCreating(c => !c)} className="bg-[#050303] text-white pill-btn hover:bg-[#bf858e] flex items-center gap-2" data-testid="new-boost-btn">
          <Plus size={16} /> New Boost
        </button>
      </header>

      {creating && (
        <form onSubmit={submit} className="core-card p-6 space-y-4" data-testid="boost-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Title</Label><Input value={form.title} onChange={v => setForm({ ...form, title: v })} required testId="b-title" placeholder="Buckhead Modern Penthouse" /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={v => setForm({ ...form, address: v })} required testId="b-address" placeholder="2965 Pharr Ct S NW, Atlanta, GA" /></div>
            <div><Label>Price</Label><Input value={form.price} onChange={v => setForm({ ...form, price: v })} type="number" required testId="b-price" placeholder="1450000" /></div>
            <div><Label>Beds / Baths / Sqft</Label>
              <div className="flex gap-2">
                <input type="number" value={form.beds} onChange={e => setForm({ ...form, beds: e.target.value })} placeholder="Beds" className="w-1/3 px-3 py-2 border border-[#d1cece] rounded-md text-sm" data-testid="b-beds" />
                <input type="number" step="0.5" value={form.baths} onChange={e => setForm({ ...form, baths: e.target.value })} placeholder="Baths" className="w-1/3 px-3 py-2 border border-[#d1cece] rounded-md text-sm" data-testid="b-baths" />
                <input type="number" value={form.sqft} onChange={e => setForm({ ...form, sqft: e.target.value })} placeholder="Sqft" className="w-1/3 px-3 py-2 border border-[#d1cece] rounded-md text-sm" data-testid="b-sqft" />
              </div>
            </div>
            <div><Label>Alert email (where new leads notify)</Label><Input value={form.agent_email} onChange={v => setForm({ ...form, agent_email: v })} testId="b-agent-email" placeholder="you@example.com — leave blank to use your account email" /></div>
            <div><Label>Agent phone (public)</Label><Input value={form.agent_phone} onChange={v => setForm({ ...form, agent_phone: v })} testId="b-agent-phone" /></div>
            <div><Label>Agent name (on flyer)</Label><Input value={form.agent_name} onChange={v => setForm({ ...form, agent_name: v })} testId="b-agent-name" placeholder="e.g. Latoya Winfrey" /></div>
            <div><Label>Agent title (on flyer)</Label><Input value={form.agent_title} onChange={v => setForm({ ...form, agent_title: v })} testId="b-agent-title" placeholder="e.g. Listing Broker" /></div>
            <div><Label>Property type (on flyer)</Label><Input value={form.property_type} onChange={v => setForm({ ...form, property_type: v })} testId="b-property-type" placeholder="e.g. Craftsman · Modern Villa" /></div>
            <div><Label>Agent headshot URL (transparent PNG)</Label><Input value={form.agent_headshot_url} onChange={v => setForm({ ...form, agent_headshot_url: v })} testId="b-agent-headshot" placeholder="https://… cutout PNG works best" /></div>
            <div className="md:col-span-2"><Label>Public MLS Link (optional)</Label><Input value={form.listing_url} onChange={v => setForm({ ...form, listing_url: v })} testId="b-listing-url" placeholder="Paste your Georgia MLS listing URL (e.g. https://www.gamls.com/…)" /></div>
          </div>
          <div><Label>Description</Label>
            <textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm" data-testid="b-desc" />
          </div>
          <div>
            <Label>Hero image</Label>
            <ImagePicker value={form.images[0] || ""} onChange={(v) => setForm({ ...form, images: v ? [v] : [] })} testIdPrefix="b-img" />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-[#050303] text-white pill-btn" data-testid="b-submit">Create Boost</button>
            <button type="button" onClick={() => setCreating(false)} className="border border-[#050303] pill-btn">Cancel</button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {boosts.length === 0 && !creating && (
          <div className="col-span-full text-center text-sm text-[#050303]/55 py-10">No boosts yet — create your first shareable listing above.</div>
        )}
        {boosts.map(b => (
          <div key={b.id} className="core-card p-5 flex flex-col" data-testid={`boost-${b.id}`}>
            {b.images?.[0] && <img src={b.images[0]} alt="" className="w-full h-36 object-cover rounded-md mb-3" />}
            <div className="font-heading text-xl">{b.title}</div>
            <div className="text-xs text-[#050303]/60 mt-1">{b.address}</div>
            <div className="font-heading text-2xl text-[#bf858e] mt-2">{fmtMoney(b.price)}</div>
            <div className="flex items-center gap-4 text-xs text-[#050303]/70 mt-2">
              <span className="flex items-center gap-1"><Eye size={12} /> {b.views || 0} views</span>
              <span className="flex items-center gap-1"><UsersIcon size={12} /> {b.leads || 0} leads</span>
            </div>
            <div className="mt-3 space-y-1.5">
              <InlineBoostField boost={b} field="listing_url" icon={LinkIcon} label="MLS link"
                placeholder="Paste your Georgia MLS listing URL" type="url" onSaved={load} />
              <InlineBoostField boost={b} field="agent_email" icon={AtSign} label="Alert email"
                placeholder="you@corerealtyandassociates.com" type="email" onSaved={load} />
              <InlineBoostField boost={b} field="agent_phone" icon={Phone} label="Public phone"
                placeholder="404-555-0100" type="tel" onSaved={load} />
            </div>
            <div className="mt-4 pt-4 border-t border-[#d1cece] flex flex-wrap gap-2 items-center">
              <button onClick={() => copyLink(b.slug)} className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] flex items-center gap-1" data-testid={`copy-${b.id}`}><Copy size={12} /> Copy</button>
              <a href={publicUrl(b.slug)} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] flex items-center gap-1" data-testid={`open-${b.id}`}><ExternalLink size={12} /> Open</a>
              <button onClick={() => setQrFor(b)} className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] flex items-center gap-1" data-testid={`qr-${b.id}`}><QrCode size={12} /> QR</button>
              <a
                href={`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(shareUrl(b.slug))}`}
                target="_blank" rel="noopener noreferrer"
                title="Open Facebook Sharing Debugger — force a fresh scrape of your OG preview"
                className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] flex items-center gap-1"
                data-testid={`fb-debug-${b.id}`}
              ><Facebook size={12} /> Preview on FB</a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setSmsFor(b); }}
                title="Text this listing — edit the branded caption, then Messages opens with your contacts."
                className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] flex items-center gap-1"
                data-testid={`sms-${b.id}`}
              ><MessageSquare size={12} /> Text listing</a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setStoryFor(b); }}
                title="Download a 1080 × 1920 vertical Story visual — pick a layout for your angle."
                className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] flex items-center gap-1"
                data-testid={`story-${b.id}`}
              ><Download size={12} /> Story visual</a>
              <ShareRow url={shareUrl(b.slug)} title={b.title} />
              <button onClick={() => del(b.id)} className="ml-auto text-[#050303]/40 hover:text-[#bf858e]" data-testid={`del-${b.id}`}><Trash2 size={13} /></button>
            </div>
            <div className="mt-3 rounded-md overflow-hidden border border-[#d1cece] bg-[#050303]/[0.02]" data-testid={`og-preview-${b.id}`}>
              <div className="px-3 pt-2 pb-1 flex items-center justify-between">
                <span className="overline text-[10px] text-[#050303]/60">Social preview</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => refreshOgPreview(b)}
                    title="Refresh preview — re-fetches the hero photo, useful after Photoshop / Lightroom edits."
                    className="text-[#bf858e] hover:text-[#050303] transition"
                    data-testid={`og-refresh-${b.id}`}
                  ><RefreshCw size={11} strokeWidth={2} /></button>
                  <span className="text-[10px] text-[#050303]/40">1200 × 630</span>
                </div>
              </div>
              <div className="relative w-full" style={{ aspectRatio: "1200 / 630" }}>
                <img
                  src={ogPreviewUrl(b)}
                  alt={`Social share preview for ${b.title || "listing"}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid={`og-preview-img-${b.id}`}
                />
              </div>
              <div className="px-3 py-2 text-[10px] text-[#050303]/55 border-t border-[#d1cece]">
                {b.images?.[0]
                  ? "Facebook & LinkedIn will show your hero photo — auto-resized to 1200 × 630 and under 5 MB."
                  : "No hero photo — using the CORE branded card. Add a photo to swap it in."}
              </div>
            </div>
            <div className="mt-3 flex justify-center bg-[#ffffff] rounded-md p-3 border border-[#d1cece]">
              <QRCodeSVG value={publicUrl(b.slug)} size={96} bgColor="#ffffff" fgColor="#050303" level="M" includeMargin={false} />
            </div>
          </div>
        ))}
      </div>

      {qrFor && <QrModal boost={qrFor} publicUrl={publicUrl(qrFor.slug)} onClose={() => setQrFor(null)} />}
      {smsFor && <SmsCaptionModal boost={smsFor} defaultCaption={defaultSmsCaption(smsFor)} onClose={() => setSmsFor(null)} />}
      {storyFor && <StoryVariantModal boost={storyFor} onClose={() => setStoryFor(null)} />}
    </div>
  );
}

function SmsCaptionModal({ boost, defaultCaption, onClose }) {
  // Persist per-boost edits in localStorage so the agent's tweaks survive reloads.
  const storageKey = `sms-caption:${boost.id}`;
  const [caption, setCaption] = useState(() => {
    try { return localStorage.getItem(storageKey) || defaultCaption; }
    catch { return defaultCaption; }
  });
  const openMessages = () => {
    try { localStorage.setItem(storageKey, caption); } catch { /* private-mode fallback */ }
    // `sms:?&body=` works on iOS + Android. Use a real navigation so the OS
    // link handler fires (window.open is blocked on some mobile browsers).
    window.location.href = `sms:?&body=${encodeURIComponent(caption)}`;
    onClose();
  };
  const reset = () => {
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    setCaption(defaultCaption);
  };
  return (
    <div className="fixed inset-0 bg-[#050303]/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="sms-modal">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="overline">SMS Caption</div>
            <h3 className="font-heading text-2xl mt-1">Text this listing</h3>
          </div>
          <button onClick={onClose} className="text-[#050303]/50 hover:text-[#050303]" data-testid="sms-close"><XIcon size={18} /></button>
        </div>
        <p className="text-xs text-[#050303]/60 mb-3">
          Tweak the caption if you want. Messages opens on your phone with this text — you pick recipients from your contacts.
        </p>
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          rows={7}
          className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm focus:outline-none focus:border-[#bf858e] resize-y"
          data-testid="sms-caption-input"
        />
        <div className="mt-2 flex items-center justify-between text-xs">
          <button type="button" onClick={reset} className="text-[#bf858e] hover:text-[#050303] uppercase tracking-wider" data-testid="sms-reset">
            Reset to default
          </button>
          <span className="text-[#050303]/50" data-testid="sms-char-count">{caption.length} characters</span>
        </div>
        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <button onClick={openMessages} className="flex-1 bg-[#050303] text-white pill-btn hover:bg-[#bf858e] flex items-center justify-center gap-2" data-testid="sms-open">
            <MessageSquare size={14} /> Open Messages
          </button>
          <button onClick={onClose} className="pill-btn border border-[#d1cece] text-[#050303]/70 hover:bg-[#d1cece]/20" data-testid="sms-cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const STORY_VARIANTS = [
  { id: "new-listing", label: "New Listing", heading: "NEW LISTING" },
  { id: "open-house", label: "Open House", heading: "OPEN HOUSE" },
  { id: "under-contract", label: "Under Contract", heading: "UNDER CONTRACT" },
  { id: "price-drop", label: "Price Drop", heading: "PRICE DROP" },
];

const STORY_ASPECTS = [
  { id: "feed", label: "Feed 1:1", desc: "Instagram · Facebook · LinkedIn" },
  { id: "story", label: "Story 9:16", desc: "IG · TikTok · WhatsApp Stories" },
];

function StoryVariantModal({ boost, onClose }) {
  const [variant, setVariant] = useState("new-listing");
  const [aspect, setAspect] = useState("feed");
  const [sharing, setSharing] = useState(false);
  const previewUrl = `${window.location.origin}/api/story/boost/${boost.slug}?variant=${variant}&aspect=${aspect}`;
  const landingUrl = `${window.location.origin}/l/${boost.slug}`;
  const canShareFiles = typeof navigator !== "undefined"
    && typeof navigator.share === "function"
    && typeof navigator.canShare === "function";

  const selected = STORY_VARIANTS.find((v) => v.id === variant) || STORY_VARIANTS[0];
  const selectedAspect = STORY_ASPECTS.find((a) => a.id === aspect) || STORY_ASPECTS[0];

  const download = () => {
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `${boost.slug}-${variant}-${aspect}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`${selected.label} · ${selectedAspect.label} downloading`);
  };

  const [bundling, setBundling] = useState(false);
  const downloadBundle = () => {
    setBundling(true);
    const a = document.createElement("a");
    a.href = `${window.location.origin}/api/story/boost/${boost.slug}/bundle.zip`;
    a.download = `CORE-Flyer_${boost.slug}_bundle.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("All 8 flyers packaged — ZIP downloading");
    // The zip stream fires quickly on the browser but the server-side
    // render of all 8 combos takes ~10 s. Leave the button disabled for
    // a moment so agents don't spam-click a 2 nd zip mid-generation.
    setTimeout(() => setBundling(false), 3000);
  };

  const shareToPhone = async () => {
    setSharing(true);
    try {
      const r = await fetch(previewUrl);
      if (!r.ok) throw new Error("fetch failed");
      const blob = await r.blob();
      const file = new File([blob], `${boost.slug}-${variant}-${aspect}.png`, { type: "image/png" });
      if (!navigator.canShare({ files: [file] })) {
        toast.info("Sharing files isn't supported here — downloaded instead.");
        download();
        return;
      }
      const captionMap = {
        "new-listing": "Just listed",
        "open-house": "Open house this weekend",
        "under-contract": "Under contract",
        "price-drop": "New price",
      };
      await navigator.share({
        files: [file],
        title: boost.title || boost.address || "New listing",
        text: `${captionMap[variant] || "New listing"} — ${boost.address || ""}\n${landingUrl}\n— CORE Realty and Associates`,
      });
      toast.success("Shared");
    } catch (err) {
      if (err && err.name !== "AbortError") toast.error("Share failed");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#050303]/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="story-modal">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="overline">Marketing Flyer</div>
            <h3 className="font-heading text-2xl mt-1">Pick a template and format.</h3>
          </div>
          <button onClick={onClose} className="text-[#050303]/50 hover:text-[#050303]" data-testid="story-close"><XIcon size={18} /></button>
        </div>
        <p className="text-xs text-[#050303]/60 mb-4">
          Rose header · Playfair serif · full-bleed hero · agent card at the bottom. Same look across all four templates so your feed stays cohesive.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6">
          {/* Live preview */}
          <div className="bg-[#050303]/[0.02] rounded-md border border-[#d1cece] overflow-hidden flex items-center justify-center min-h-[420px]" data-testid="story-preview-wrap">
            <img
              key={`${variant}-${aspect}`}
              src={previewUrl}
              alt={`${selected.label} · ${selectedAspect.label} preview`}
              className={aspect === "story" ? "max-h-[560px] w-auto" : "max-w-full max-h-[560px] w-auto"}
              data-testid={`story-preview-${variant}-${aspect}`}
            />
          </div>

          <div className="space-y-4">
            <div>
              <div className="overline text-[10px] mb-2">Template</div>
              <div className="grid grid-cols-2 gap-2">
                {STORY_VARIANTS.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setVariant(v.id)}
                    className={`px-3 py-2 rounded-md border text-left transition ${
                      variant === v.id
                        ? "border-[#bf858e] bg-[#e1b7c1]/20"
                        : "border-[#d1cece] hover:border-[#bf858e]"
                    }`}
                    data-testid={`story-variant-${v.id}`}
                  >
                    <div className="font-semibold text-xs text-[#050303]">{v.label}</div>
                    <div className="text-[10px] text-[#bf858e] mt-0.5 truncate">{v.heading}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="overline text-[10px] mb-2">Format</div>
              <div className="grid grid-cols-2 gap-2">
                {STORY_ASPECTS.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setAspect(a.id)}
                    className={`px-3 py-2 rounded-md border text-left transition ${
                      aspect === a.id
                        ? "border-[#bf858e] bg-[#e1b7c1]/20"
                        : "border-[#d1cece] hover:border-[#bf858e]"
                    }`}
                    data-testid={`story-aspect-${a.id}`}
                  >
                    <div className="font-semibold text-xs text-[#050303]">{a.label}</div>
                    <div className="text-[10px] text-[#050303]/60 mt-0.5">{a.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {(!boost.agent_name || !boost.agent_headshot_url) && (
              <div className="text-[10px] text-[#050303]/60 border border-[#d1cece] bg-[#e1b7c1]/10 rounded-md px-3 py-2" data-testid="story-agent-hint">
                Tip: add {[
                  !boost.agent_name && "agent name",
                  !boost.agent_title && "title",
                  !boost.agent_headshot_url && "a transparent-BG headshot URL",
                ].filter(Boolean).join(", ")} to this boost so the flyer&apos;s bottom card looks complete.
              </div>
            )}

            <div className="space-y-2 pt-2">
              {canShareFiles && (
                <button
                  onClick={shareToPhone}
                  disabled={sharing}
                  className="w-full bg-[#bf858e] text-white pill-btn hover:bg-[#050303] flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="story-share"
                >
                  <Share2 size={14} /> {sharing ? "Preparing…" : "Share to phone"}
                </button>
              )}
              <button
                onClick={download}
                className="w-full bg-[#050303] text-white pill-btn hover:bg-[#bf858e] flex items-center justify-center gap-2"
                data-testid="story-download"
              >
                <Download size={14} /> Download {selected.label}
              </button>
              <button
                onClick={downloadBundle}
                disabled={bundling}
                className="w-full border border-[#050303] text-[#050303] pill-btn hover:bg-[#050303] hover:text-white flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="story-download-all"
                title="Get all 4 templates × 2 formats as one ZIP"
              >
                <Package size={14} /> {bundling ? "Packaging 8 flyers…" : "Download all 8 as ZIP"}
              </button>
            </div>
            <p className="text-[10px] text-[#050303]/50">
              {selectedAspect.id === "feed" ? "1080 × 1080 · PNG" : "1080 × 1920 · PNG"} · Ready for {selectedAspect.desc.split(" · ")[0]}.
              {canShareFiles ? " Tap Share to post straight from your phone." : ""}
              {" "}The ZIP includes every template × format combo.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


function QrModal({ boost, publicUrl, onClose }) {
  const download = () => {
    const canvas = document.getElementById(`qr-canvas-${boost.id}`);
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `core-boost-${boost.slug}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  return (
    <div className="fixed inset-0 bg-[#050303]/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="qr-modal">
      <div className="bg-white rounded-lg p-8 max-w-sm w-full text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="overline mb-1">Property Boost QR</div>
        <h3 className="font-heading text-2xl mb-4">{boost.title}</h3>
        <div className="flex justify-center mb-4">
          <QRCodeCanvas id={`qr-canvas-${boost.id}`} value={publicUrl} size={260} bgColor="#ffffff" fgColor="#050303" level="H" includeMargin={true} />
        </div>
        <p className="text-xs text-[#050303]/60 mb-5 break-all">{publicUrl}</p>
        <div className="flex gap-2 justify-center">
          <button onClick={download} className="bg-[#050303] text-white pill-btn text-sm flex items-center gap-2" data-testid="qr-download"><Download size={14} /> Download PNG</button>
          <button onClick={onClose} className="border border-[#050303] pill-btn text-sm">Close</button>
        </div>
        <p className="text-[10px] text-[#050303]/50 mt-4">Perfect for yard-sign toppers, flyers, and open-house handouts.</p>
      </div>
    </div>
  );
}

function emptyForm() {
  return { title: "", address: "", price: "", beds: "", baths: "", sqft: "", property_type: "", description: "", images: [], agent_email: "", agent_phone: "", agent_name: "", agent_title: "", agent_headshot_url: "", listing_url: "" };
}

function Label({ children }) { return <label className="text-xs uppercase tracking-wider text-[#050303]/70 block mb-1">{children}</label>; }
function Input({ value, onChange, type = "text", required = false, testId, placeholder = "" }) {
  return <input type={type} required={required} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} data-testid={testId} className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm" />;
}

function ShareRow({ url, title }) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  const links = [
    { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${u}`, icon: "https://cdn.simpleicons.org/facebook/bf858e" },
    { name: "X", href: `https://twitter.com/intent/tweet?url=${u}&text=${t}`, icon: "https://cdn.simpleicons.org/x/bf858e" },
    { name: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`, icon: "https://cdn.simpleicons.org/linkedin/bf858e" },
    { name: "WhatsApp", href: `https://api.whatsapp.com/send?text=${t}%20${u}`, icon: "https://cdn.simpleicons.org/whatsapp/bf858e" },
    { name: "Email", href: `mailto:?subject=${t}&body=${u}`, icon: "https://cdn.simpleicons.org/gmail/bf858e" },
  ];
  return (
    <>
      {links.map(l => (
        <a key={l.name} href={l.href} target="_blank" rel="noopener noreferrer" title={`Share on ${l.name}`}
          className="hover:opacity-70" data-testid={`share-${l.name.toLowerCase()}`}>
          <img src={l.icon} alt={l.name} className="w-4 h-4" />
        </a>
      ))}
    </>
  );
}

function InlineBoostField({ boost, field, icon: Icon, label, placeholder, type = "text", onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const current = boost[field] || "";
  const [val, setVal] = React.useState(current);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { setVal(current); }, [current]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/boosts/${boost.id}`, { [field]: val.trim() });
      toast.success(`${label} updated`);
      setEditing(false);
      onSaved?.();
    } catch { toast.error("Could not update"); }
    finally { setSaving(false); }
  };

  const cancel = () => { setVal(current); setEditing(false); };

  // Renders href for MLS link, mailto: for email, tel: for phone.
  const displayHref = () => {
    if (!current) return null;
    if (field === "agent_email") return `mailto:${current}`;
    if (field === "agent_phone") return `tel:${current.replace(/[^0-9+]/g, "")}`;
    return current;
  };

  const displayText = () => {
    if (!current) return null;
    if (field === "listing_url") return current.replace(/^https?:\/\//, "");
    return current;
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2 text-xs" data-testid={`row-${field}-${boost.id}`}>
        <Icon size={11} className="text-[#bf858e] shrink-0" />
        {current ? (
          <>
            <a href={displayHref()} target={field === "listing_url" ? "_blank" : undefined} rel="noopener noreferrer"
              className="text-[#050303] hover:text-[#bf858e] underline underline-offset-2 truncate flex-1"
              title={current}
              data-testid={`view-${field}-${boost.id}`}>
              {displayText()}
            </a>
            <button onClick={() => setEditing(true)}
              className="uppercase tracking-wider text-[#bf858e] hover:text-[#050303] shrink-0"
              data-testid={`edit-${field}-${boost.id}`}>Edit</button>
          </>
        ) : (
          <button onClick={() => setEditing(true)}
            className="uppercase tracking-wider text-[#bf858e] hover:text-[#050303]"
            data-testid={`add-${field}-${boost.id}`}>+ Add {label.toLowerCase()}</button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid={`editor-${field}-${boost.id}`}>
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={placeholder}
        type={type}
        className="w-full px-2.5 py-1.5 border border-[#d1cece] rounded-md text-xs"
        data-testid={`input-${field}-${boost.id}`}
        autoFocus
      />
      <div className="flex items-center gap-2 text-xs">
        <button onClick={save} disabled={saving}
          className="pill-btn text-xs bg-[#050303] text-white hover:bg-[#bf858e] disabled:opacity-40"
          data-testid={`save-${field}-${boost.id}`}>
          <Save size={11} className="inline mr-1" /> {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={cancel} className="text-[#050303]/50 hover:text-[#bf858e]"
          data-testid={`cancel-${field}-${boost.id}`}>
          <XIcon size={12} className="inline" /> Cancel
        </button>
      </div>
    </div>
  );
}

